/**
 * 运维：任务队列 / 事务性 outbox / 入站回调。
 *
 * 三张表当前都是空的——worker 与 scheduler 尚未实现。
 * 但监控项已经就位：租约过期是 worker 崩溃的信号，
 * outbox 积压意味着副作用没发出去，签名校验失败意味着有人在伪造回调。
 */

import { Select, Space, Tabs, Typography } from "antd";
import { useState } from "react";
import { DataTable, useTableParams } from "../../components/DataTable";
import { fmtDateTime, RelativeTime, ShortId, StatusTag } from "../../components/format";

export function OperationsPage() {
  const [tab, setTab] = useState("jobs");
  return (
    <Tabs
      activeKey={tab}
      onChange={setTab}
      items={[
        { key: "jobs", label: "任务队列", children: <Jobs /> },
        { key: "outbox", label: "Outbox 事件", children: <OutboxTable /> },
        { key: "webhooks", label: "入站回调", children: <Webhooks /> },
      ]}
    />
  );
}

const JOB_FILTERS = ["status", "kind"];

function Jobs() {
  const { searchParams, setParam } = useTableParams(JOB_FILTERS);
  return (
    <>
      <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
        租约（lease）机制防止同一个任务被多个 worker 同时执行。
        `leaseExpiresAt` 已过期但状态仍是 leased/running，说明 worker 崩了，需要人工回收。
      </Typography.Paragraph>
      <DataTable
        path="/api/v1/admin/operations/jobs"
        filterKeys={JOB_FILTERS}
        toolbar={
          <Space wrap>
            <Select
              allowClear
              style={{ width: 150 }}
              placeholder="状态"
              value={searchParams.get("status") ?? undefined}
              onChange={(v) => setParam("status", v)}
              options={["pending", "leased", "running", "succeeded", "failed", "dead"].map((v) => ({
                value: v,
                label: v,
              }))}
            />
          </Space>
        }
        columns={[
          { title: "任务", dataIndex: "id", width: 90, render: (v) => <ShortId value={v} /> },
          { title: "类型", dataIndex: "kind", width: 200 },
          { title: "调度键", dataIndex: "scheduleKey", width: 200, render: (v) => v || "—" },
          { title: "状态", dataIndex: "status", width: 110, render: (v) => <StatusTag value={v} /> },
          { title: "执行于", dataIndex: "runAt", width: 170, render: fmtDateTime },
          { title: "租约持有者", dataIndex: "leaseOwner", width: 140, render: (v) => v || "—" },
          { title: "租约过期", dataIndex: "leaseExpiresAt", width: 170, render: fmtDateTime },
          {
            title: "重试",
            width: 90,
            align: "right",
            render: (_: unknown, r: { attempts: number; maxAttempts: number }) =>
              `${r.attempts}/${r.maxAttempts}`,
          },
          { title: "最后错误", dataIndex: "lastError", render: (v) => v || "—" },
        ]}
      />
    </>
  );
}

const OUTBOX_FILTERS = ["status"];

function OutboxTable() {
  const { searchParams, setParam } = useTableParams(OUTBOX_FILTERS);
  return (
    <>
      <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
        事务性发件箱：同事务内的副作用先写 outbox，由 worker 可靠投递。
        pending 堆积说明投递环节出了问题——副作用没有丢，但也没有发出去。
      </Typography.Paragraph>
      <DataTable
        path="/api/v1/admin/operations/outbox"
        filterKeys={OUTBOX_FILTERS}
        toolbar={
          <Select
            allowClear
            style={{ width: 150 }}
            placeholder="状态"
            value={searchParams.get("status") ?? undefined}
            onChange={(v) => setParam("status", v)}
            options={["pending", "published", "failed"].map((v) => ({ value: v, label: v }))}
          />
        }
        columns={[
          { title: "事件", dataIndex: "id", width: 90, render: (v) => <ShortId value={v} /> },
          { title: "聚合类型", dataIndex: "aggregateType", width: 160 },
          { title: "聚合 ID", dataIndex: "aggregateId", width: 100, render: (v) => <ShortId value={v} /> },
          { title: "事件类型", dataIndex: "eventType", width: 220 },
          { title: "状态", dataIndex: "status", width: 110, render: (v) => <StatusTag value={v} /> },
          { title: "重试", dataIndex: "attempts", width: 70, align: "right" },
          { title: "最后错误", dataIndex: "lastError", render: (v) => v || "—" },
          { title: "发布于", dataIndex: "publishedAt", width: 170, render: fmtDateTime },
        ]}
      />
    </>
  );
}

function Webhooks() {
  return (
    <>
      <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
        统一的入站回调日志（微信支付、激励视频等）。`signatureValid = false` 的记录要重点看——
        要么是密钥配错，要么有人在伪造回调。原始报文只存引用（rawPayloadRef），不入库。
      </Typography.Paragraph>
      <DataTable
        path="/api/v1/admin/operations/webhooks"
        columns={[
          { title: "记录", dataIndex: "id", width: 90, render: (v) => <ShortId value={v} /> },
          { title: "渠道", dataIndex: "provider", width: 140 },
          { title: "事件类型", dataIndex: "eventType", width: 200 },
          { title: "渠道事件 ID", dataIndex: "providerEventId", width: 200 },
          {
            title: "签名",
            dataIndex: "signatureValid",
            width: 90,
            render: (v: boolean) => <StatusTag value={v ? "ok" : "critical"} />,
          },
          { title: "处理结果", dataIndex: "processResult", render: (v) => v || "—" },
          { title: "错误码", dataIndex: "errorCode", width: 130, render: (v) => v || "—" },
          { title: "接收", dataIndex: "receivedAt", width: 130, render: (v) => <RelativeTime value={v} /> },
        ]}
      />
    </>
  );
}
